import { useInfiniteQuery } from "@tanstack/react-query";
import { queryClient } from "shared/api/queryClient";
import { searchSupportTicketsIndex } from "../../algolia/constants";
import { CustomerSupportTicket as Ticket, TicketStatus } from "../types";

const HITS_PER_PAGE = 25;

type CustomerSupportTicket = Ticket & {
  objectID: string;
};

type PageData = {
  hits: CustomerSupportTicket[];
  nbHits: number;
};

type QueryData = {
  nbHits: number;
  pages: PageData[];
};

export type QueryTicketsProps = {
  filters?: string;
  query?: string;
};

export const getQueryKey = ({ filters, query }: QueryTicketsProps) => {
  return ["customerTickets", query, filters, HITS_PER_PAGE];
};

export const invalidateCustomerTicketsQuery = async ({
  filters,
  query,
}: QueryTicketsProps) => {
  setTimeout(async () => {
    await queryClient.invalidateQueries({
      queryKey: getQueryKey({ filters, query }),
    });
  }, 6000);
};

export const useCustomerTicketsDataQuery = ({
  filters,
  query,
}: QueryTicketsProps) => {
  const {
    data,
    refetch,
    isFetchingNextPage,
    isLoading,
    fetchNextPage,
    hasNextPage,
  } = useInfiniteQuery({
    queryKey: getQueryKey({ filters, query }),
    initialPageParam: 0,
    queryFn: async ({ pageParam = 0 }) => {
      const result =
        await searchSupportTicketsIndex.search<CustomerSupportTicket>("", {
          query,
          page: pageParam,
          filters,
          hitsPerPage: HITS_PER_PAGE,
          cacheable: false,
        });

      return {
        hits: result.hits,
        nbHits: result.nbHits,
      };
    },
    getNextPageParam: (lastPage, pages) => {
      const nextPage = pages?.length + 1;
      return lastPage.nbHits > lastPage.hits?.length ? nextPage : undefined;
    },
  });

  const modifyTicketData = (
    ticketId: string,
    modifyFn: (ticket: CustomerSupportTicket) => CustomerSupportTicket,
    moveToTop: boolean = false,
  ) => {
    queryClient.setQueryData<QueryData>(
      getQueryKey({ filters, query }),
      (oldData) => {
        if (!oldData) return oldData;

        const allHits = oldData.pages.flatMap((page) => page.hits);

        const ticketIndex = allHits.findIndex(
          (ticket) => ticket.objectID === ticketId,
        );

        if (ticketIndex === -1) return oldData;

        const updatedTicket = modifyFn(allHits[ticketIndex]);

        const filteredTickets = allHits.filter(
          (ticket) => ticket.objectID !== ticketId,
        );

        const reorderedTickets = moveToTop
          ? [updatedTicket, ...filteredTickets]
          : [
              ...filteredTickets.slice(0, ticketIndex),
              updatedTicket,
              ...filteredTickets.slice(ticketIndex),
            ];

        const pageSize = HITS_PER_PAGE || 10;
        const newPages = [];
        for (let i = 0; i < reorderedTickets.length; i += pageSize) {
          const pageHits = reorderedTickets.slice(i, i + pageSize);
          newPages.push({ hits: pageHits, nbHits: oldData.nbHits });
        }

        return { ...oldData, pages: newPages };
      },
    );
  };

  const updateCustomerTicketStatus = (
    ticketId: string,
    newStatus: TicketStatus,
  ) => {
    modifyTicketData(
      ticketId,
      (ticket) => ({
        ...ticket,
        status: newStatus,
        updatedAt: new Date(),
      }),
      true,
    );
  };

  const updateTicketAdminUnreadMessagesCount = (ticketId: string) => {
    modifyTicketData(ticketId, (ticket) => ({
      ...ticket,
      adminUnreadMessagesCount: 0,
    }));
  };

  const moveTicketToTop = (ticketId: string) => {
    modifyTicketData(
      ticketId,
      (ticket) => ({
        ...ticket,
        adminUnreadMessagesCount: 0,
        updatedAt: new Date(),
      }),
      true,
    );
  };

  return {
    data,
    refetch,
    moveTicketToTop,
    updateCustomerTicketStatus,
    updateTicketAdminUnreadMessagesCount,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    isLoading,
  };
};
